/**
 * SDKContext — fournit l'instance AssistiaSDK à toute l'app admin.
 *
 * Mode démonstration (best-effort, SLA bas) vs production :
 *   - `VITE_DEMO_MODE=true` → injecte le SimulationProvider + demoMode=true :
 *     un backend indisponible (circuit ouvert) rejoue des données fictives et
 *     `isSimulated` passe à true (bannière démo).
 *   - sinon (PROD, défaut) → AUCUN provider démo, demoMode=false : un backend
 *     indisponible lève `BackendUnavailableError` (pas de données fabriquées) ;
 *     `isSimulated` reste false, les pages affichent un état d'erreur réel.
 */
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { AssistiaSDK } from '@assistia/sdk';
import type { SimulationProvider, DemoScenario } from '@assistia/sdk';

const POLL_INTERVAL_MS = 2_000;

/** Mode démo activé uniquement par un build/déploiement démo explicite. */
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

class LazySimulationProvider implements SimulationProvider {
  private delegate: SimulationProvider | null = null;

  setDelegate(delegate: SimulationProvider) {
    this.delegate = delegate;
  }

  getDossier(id?: string): Record<string, unknown> {
    return this.delegate ? this.delegate.getDossier(id) : { id: 'demo-dossier', status: 'pending' };
  }

  listDossiers(): Record<string, unknown>[] {
    return this.delegate ? this.delegate.listDossiers() : [];
  }

  getScenario(dossierId?: string): DemoScenario {
    return this.delegate ? this.delegate.getScenario(dossierId) : { id: 'empty', dossier: {}, events: [] };
  }

  getAdminData(resource: string): Record<string, unknown>[] {
    return this.delegate ? this.delegate.getAdminData(resource) : [];
  }
}

// Instanciation du lazy provider uniquement si le mode démo est activé
const lazyProvider = DEMO_MODE ? new LazySimulationProvider() : undefined;

interface SDKContextValue {
  sdk: AssistiaSDK;
  /** True uniquement en mode démo lorsque le circuit est ouvert (données fictives). */
  isSimulated: boolean;
  /** True si l'app tourne en mode démonstration (best-effort). Absent = prod. */
  demoMode?: boolean;
  /** Forcer une demi-ouverture (bouton Reconnecter). */
  reconnect: () => void;
}

const SDKContext = createContext<SDKContextValue | null>(null);

export function SDKProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const sdk = useMemo(() => new AssistiaSDK({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
    demoMode: DEMO_MODE,
    // Provider de simulation lazy injecté uniquement en mode démo
    ...(lazyProvider ? { simulationProvider: lazyProvider } : {}),
  }), []);

  if (typeof window !== 'undefined') {
    (window as any).sdk = sdk;
  }

  const [isSimulated, setIsSimulated] = useState<boolean>(
    DEMO_MODE || sdk.circuitBreaker.getState() === 'Open'
  );

  // Chargement asynchrone des données de simulation uniquement en mode démo
  useEffect(() => {
    if (DEMO_MODE && lazyProvider) {
      import('@assistia/demo-data')
        .then(({ DemoSimulationProvider }) => {
          lazyProvider.setDelegate(new DemoSimulationProvider());
        })
        .catch(err => {
          console.error('Erreur de chargement asynchrone pour demo-data :', err);
        });
    }
  }, []);

  // Polling de l'état du circuit breaker
  useEffect(() => {
    if (DEMO_MODE) {
      setIsSimulated(true);
      return;
    }
    const id = setInterval(() => {
      setIsSimulated(sdk.circuitBreaker.getState() === 'Open');
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [sdk]);

  // Note : CircuitBreaker Gemini n'expose pas de reset() public.
  // Le reconnect force un état HalfOpen au prochain appel via expiry du openTimeout.
  const reconnect = useCallback(() => {
    // Déclencher un appel de test (fire-and-forget) pour passer en HalfOpen
    sdk.admin.getHealthDeep().catch(() => {/* ignore — le CB gère lui-même */});
    setIsSimulated(sdk.circuitBreaker.getState() === 'Open');
  }, [sdk]);

  return (
    <SDKContext.Provider value={{ sdk, isSimulated, demoMode: DEMO_MODE, reconnect }}>
      {children}
    </SDKContext.Provider>
  );
}

export function useSDK(): SDKContextValue {
  const ctx = useContext(SDKContext);
  if (!ctx) throw new Error('useSDK must be used within SDKProvider');
  return ctx;
}
